import { useState } from 'react'
import { api } from '../../api'

/**
 * Coordonnees et informations legales de l'organisation, dans « Mon
 * organisation » (ex-« Mon profil producteur »).
 *
 * POURQUOI CE COMPOSANT EXISTE (S61)
 * ----------------------------------
 * Le compte producteur affichait DEUX entrees : « Mon profil producteur » (la
 * fiche publique — logo, enseigne, commune, presentation, certifications) et
 * « Mon organisation » (telephone, e-mail de contact, adresse, site web, SIRET).
 * Enzo demande de n'en garder qu'une, au design du prototype, et il la pensait
 * complete. Elle ne l'etait pas : le SIRET, le telephone et l'adresse ne
 * vivaient QUE dans l'ancienne page. La supprimer telle quelle aurait retire le
 * seul chemin d'edition de donnees de facturation.
 *
 * Ces champs descendent donc ici. La page unique porte enfin son nom.
 *
 * CE QUI EST PUBLIC ET CE QUI NE L'EST PAS
 * ----------------------------------------
 * Le reste de la page est la fiche vue par les clients. Ces coordonnees-ci ne le
 * sont pas : elles servent la facturation et les versements. Le composant le dit
 * explicitement, sinon on ne peut pas le deviner en les voyant cote a cote.
 */

const CHAMPS = [
  { key: 'telephone', label: 'Téléphone', placeholder: '0696 XX XX XX' },
  { key: 'email', label: 'Email de contact', placeholder: 'contact@monactivite.fr' },
  { key: 'adresseRue', label: 'Adresse', placeholder: 'Rue...' },
  { key: 'codePostal', label: 'Code postal', placeholder: '97200' },
  { key: 'siteWeb', label: 'Site web', placeholder: 'https://...' },
]

/** Un e-mail genere a l'inscription par telephone n'est pas une vraie adresse. */
const vraiEmail = (v) => (v && !v.endsWith('@phone.manrina.local') ? v : '')

function CoordonneesSection({ org, onRefresh, onToast }) {
  const [ouvert, setOuvert] = useState(false)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [erreur, setErreur] = useState(null)

  if (!org) return null

  const ouvrir = () => {
    setForm({
      telephone: org.telephone || org.owner?.telephone || '',
      email: vraiEmail(org.email) || vraiEmail(org.owner?.email) || '',
      adresseRue: org.adresseRue || '',
      codePostal: org.codePostal || '',
      siteWeb: org.siteWeb || '',
    })
    setErreur(null)
    setOuvert(true)
  }

  const enregistrer = async () => {
    setSaving(true)
    setErreur(null)
    try {
      await api.producer.updateMe(form)
      setOuvert(false)
      onToast?.('Coordonnées mises à jour ✓')
      onRefresh?.()
    } catch (err) {
      setErreur(err.message || "L'enregistrement a échoué — réessaie.")
    } finally {
      setSaving(false)
    }
  }

  const lignes = [
    org.siret && { label: 'SIRET', value: org.siret },
    vraiEmail(org.email) && { label: 'Email', value: org.email },
    org.telephone && { label: 'Téléphone', value: org.telephone },
    [org.adresseRue, org.codePostal].filter(Boolean).length
      && { label: 'Adresse', value: [org.adresseRue, org.codePostal].filter(Boolean).join(', ') },
    org.siteWeb && { label: 'Site web', value: org.siteWeb },
  ].filter(Boolean)

  return (
    <div className="panneau-verre rounded-[22px] p-4 mt-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink">Coordonnées & informations légales</p>
          <p className="text-[11.5px] text-ink/55 mt-0.5">
            Jamais publiques — elles servent à la facturation et aux versements.
          </p>
        </div>
        <button
          onClick={ouvert ? () => setOuvert(false) : ouvrir}
          className="text-[12px] font-extrabold rounded-full px-3.5 py-2 flex-shrink-0 text-ink"
          style={{ background: 'linear-gradient(120deg, rgb(var(--c-primary) / 0.16), rgb(var(--c-teal) / 0.12))' }}
        >
          {ouvert ? 'Annuler' : 'Modifier'}
        </button>
      </div>

      {!ouvert && (
        lignes.length ? (
          <div className="mt-3 flex flex-col gap-2">
            {lignes.map((l) => (
              <div key={l.label} className="flex items-baseline gap-2">
                <span className="text-[11px] uppercase tracking-wider text-ink/45 w-[72px] flex-shrink-0">{l.label}</span>
                <span className="text-[13px] text-ink break-words min-w-0">{l.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12.5px] text-ink/50 mt-3">
            Aucune coordonnée renseignée. Elles seront demandées pour vos factures.
          </p>
        )
      )}

      {ouvert && (
        <div className="mt-3 flex flex-col gap-2.5">
          {CHAMPS.map((c) => (
            <label key={c.key} className="block">
              <span className="block text-[11px] uppercase tracking-wider text-ink/45 mb-1">{c.label}</span>
              <input
                value={form[c.key] || ''}
                onChange={(e) => setForm({ ...form, [c.key]: e.target.value })}
                placeholder={c.placeholder}
                className="w-full rounded-xl px-3.5 py-2.5 text-[13.5px] text-ink bg-ink/[0.04] border border-ink/10 outline-none"
              />
            </label>
          ))}
          {erreur && <p className="text-[12px] text-red-600">{erreur}</p>}
          <button
            onClick={enregistrer}
            disabled={saving}
            className="w-full rounded-full py-3 text-[13.5px] font-extrabold text-ink disabled:opacity-50"
            style={{ background: 'rgb(var(--c-primary))' }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      )}
    </div>
  )
}

export default CoordonneesSection
